import React, { useCallback, useState } from 'react';

import { Button, ButtonGroup, Card, IconButton, Skeleton, Typography } from '@mui/material';
import { CirclePlus, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { AccountOption } from 'src/components/AccountsAutocomplete';
import { AccountsAutocomplete } from 'src/components/AccountsAutocomplete';
import { AlineTextarea } from 'src/components/AlineTextarea';
import { AlineButton, AlineTooltip, PanelHeader, Spacer } from 'src/components/generic';
import { IconColors } from 'src/components/icons/icon-utils';
import { WhatsThis } from 'src/components/WhatsThis';
import {
  useCollabCardStyles,
  WorkflowCollaboratorCard,
  WorkflowCollaboratorCardVariant,
} from 'src/components/workflow';
import {
  sortCollaborators,
  TemplateCollaboratorsTab,
} from 'src/components/workflow/workflow-builder/WorkflowTeam';
import { Apollo } from 'src/gql/generated';
import { useAddWorkflowPermissions, useGetOrgMembers, useGetWorkflowPermissions } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import {
  getIsAccountDisabledForWorkflowPermissionAdd,
  getWorkflowPermissionAddPlan,
} from './collaborator-add-behavior';
import { useWorkflowDetailLinks } from '../WorkflowDetailContext';

const PANEL_STATE = {
  addCollaborator: 'addCollaborator',
  content: 'content',
} as const;

type PanelState = (typeof PANEL_STATE)[keyof typeof PANEL_STATE];

const useStyles = makeStyles()(theme => ({
  addForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(0, 0.5),
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(1),
  },
  emptyState: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    height: '100%',
    justifyContent: 'center',
  },
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
  },
  submitRow: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: '1fr 1fr',
  },
  tabRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
  },
}));

interface CollaboratorsPanelProps {
  workflowId: UUID;
}

export const CollaboratorsPanel = React.memo(function CollaboratorsPanel({
  workflowId,
}: CollaboratorsPanelProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const links = useWorkflowDetailLinks(workflowId);
  const [panelState, setPanelState] = useState<PanelState>(PANEL_STATE.content);
  const [templateTab, setTemplateTab] = useState<TemplateCollaboratorsTab>(
    TemplateCollaboratorsTab.TemplateCollaborators
  );
  const { loading, permissions } = useGetWorkflowPermissions(workflowId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const variant = links.workflow?.variant ?? null;
  const isTemplate = variant === Apollo.WorkflowVariant.Template;
  const collaboratorPermissions = permissions.filter(getIsHumanCollaboratorPermission);

  const displayedPermissions = isTemplate
    ? collaboratorPermissions.filter(p =>
        templateTab === TemplateCollaboratorsTab.TemplateCollaborators
          ? p.timeliness === Apollo.WorkflowPermissionTimeliness.Active
          : p.timeliness === Apollo.WorkflowPermissionTimeliness.Queued
      )
    : collaboratorPermissions;

  const timeliness =
    isTemplate && templateTab === TemplateCollaboratorsTab.WorkflowCollaborators
      ? Apollo.WorkflowPermissionTimeliness.Queued
      : Apollo.WorkflowPermissionTimeliness.Active;

  const handleBack = useCallback(() => {
    if (panelState === PANEL_STATE.addCollaborator) {
      setPanelState(PANEL_STATE.content);
      return;
    }
    navigate(links.baseUrl());
  }, [links, navigate, panelState]);

  return (
    <div className={classes.root}>
      <PanelHeader
        showBackButton
        text={panelState === PANEL_STATE.addCollaborator ? 'Add Collaborators' : 'Collaborators'}
        onClickBack={handleBack}
      >
        <Spacer />
        <CollaboratorsPanelAddButton
          isTemplate={isTemplate}
          panelState={panelState}
          templateTab={templateTab}
          onAddCollaborator={() => setPanelState(PANEL_STATE.addCollaborator)}
        />
      </PanelHeader>
      {isTemplate && <TemplateCollaboratorsTabRow value={templateTab} onChange={setTemplateTab} />}
      <div className={classes.content}>
        <CollaboratorsPanelContent
          allPermissions={permissions}
          displayedPermissions={displayedPermissions}
          isTemplate={isTemplate}
          loading={loading}
          panelState={panelState}
          templateTab={isTemplate ? templateTab : undefined}
          timeliness={timeliness}
          workflowId={workflowId}
          onAddCollaborator={() => setPanelState(PANEL_STATE.addCollaborator)}
          onAdded={() => setPanelState(PANEL_STATE.content)}
        />
      </div>
    </div>
  );
});

function getIsHumanCollaboratorPermission(
  permission: ReturnType<typeof useGetWorkflowPermissions>['permissions'][number]
): boolean {
  return Boolean(permission.account || permission.role);
}

function TemplateCollaboratorsTabRow({
  onChange,
  value,
}: {
  onChange: (tab: TemplateCollaboratorsTab) => void;
  value: TemplateCollaboratorsTab;
}): JSX.Element {
  const { classes } = useStyles();
  return (
    <div className={classes.tabRow}>
      <ButtonGroup color="primary" size="small">
        <Button
          variant={
            value === TemplateCollaboratorsTab.TemplateCollaborators ? 'contained' : 'outlined'
          }
          onClick={() => onChange(TemplateCollaboratorsTab.TemplateCollaborators)}
        >
          Template
        </Button>
        <Button
          variant={
            value === TemplateCollaboratorsTab.WorkflowCollaborators ? 'contained' : 'outlined'
          }
          onClick={() => onChange(TemplateCollaboratorsTab.WorkflowCollaborators)}
        >
          Workflow
        </Button>
      </ButtonGroup>
      <WhatsThis
        tooltipMsg='"Template collaborators" have access to the template and can launch or edit, depending on access. "Workflow collaborators" receive access to any workflow launched from this template.'
        tooltipPlacement="right"
      />
    </div>
  );
}

function CollaboratorsPanelAddButton({
  isTemplate,
  onAddCollaborator,
  panelState,
  templateTab,
}: {
  isTemplate: boolean;
  onAddCollaborator: () => void;
  panelState: PanelState;
  templateTab: TemplateCollaboratorsTab;
}) {
  if (panelState !== PANEL_STATE.content) return null;

  const addButtonLabel = isTemplate
    ? templateTab === TemplateCollaboratorsTab.TemplateCollaborators
      ? 'Add template collaborator'
      : 'Add workflow collaborator'
    : 'Add collaborator';

  return (
    <AlineTooltip arrow placement="top" title={addButtonLabel}>
      <IconButton size="small" onClick={onAddCollaborator}>
        <CirclePlus size={20} />
      </IconButton>
    </AlineTooltip>
  );
}

function CollaboratorsPanelContent({
  allPermissions,
  displayedPermissions,
  isTemplate,
  loading,
  onAddCollaborator,
  onAdded,
  panelState,
  templateTab,
  timeliness,
  workflowId,
}: {
  allPermissions: ReturnType<typeof useGetWorkflowPermissions>['permissions'];
  displayedPermissions: ReturnType<typeof useGetWorkflowPermissions>['permissions'];
  isTemplate: boolean;
  loading: boolean;
  onAddCollaborator: () => void;
  onAdded: () => void;
  panelState: PanelState;
  templateTab: TemplateCollaboratorsTab | undefined;
  timeliness: Apollo.WorkflowPermissionTimeliness;
  workflowId: UUID;
}) {
  if (loading) return <CollaboratorsLoading />;

  switch (panelState) {
    case PANEL_STATE.content:
      if (!displayedPermissions.length)
        return (
          <CollaboratorsEmptyState
            addButtonLabel={
              isTemplate && templateTab !== undefined
                ? templateTab === TemplateCollaboratorsTab.TemplateCollaborators
                  ? 'Add template collaborator'
                  : 'Add workflow collaborator'
                : 'Add collaborator'
            }
            onAddCollaborator={onAddCollaborator}
          />
        );
      return (
        <CollaboratorsList
          permissions={displayedPermissions}
          templateTab={templateTab}
          workflowId={workflowId}
        />
      );
    case PANEL_STATE.addCollaborator:
      return (
        <AddCollaboratorContent
          addButtonLabel={
            isTemplate
              ? timeliness === Apollo.WorkflowPermissionTimeliness.Active
                ? 'Add Template Collaborator'
                : 'Add Workflow Collaborator'
              : 'Add Collaborator'
          }
          permissions={allPermissions}
          timeliness={timeliness}
          workflowId={workflowId}
          workflowVariant={
            isTemplate ? Apollo.WorkflowVariant.Template : Apollo.WorkflowVariant.Contract
          }
          onAdded={onAdded}
        />
      );
  }
}

function CollaboratorsLoading() {
  const { classes } = useStyles();
  const { classes: cardClasses } = useCollabCardStyles();
  return (
    <div className={classes.cardList}>
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i} className={cardClasses.card}>
          <Skeleton height={40} variant="circular" width={40} />
          <Skeleton variant="text" width={64} />
          <Spacer />
          <Skeleton height={32} sx={{ borderRadius: 2 }} variant="rectangular" width="100%" />
        </Card>
      ))}
    </div>
  );
}

function CollaboratorsEmptyState({
  addButtonLabel,
  onAddCollaborator,
}: {
  addButtonLabel: string;
  onAddCollaborator: () => void;
}) {
  const { classes } = useStyles();
  return (
    <div className={classes.emptyState}>
      <UserRound color={IconColors.Primary} height={126} strokeOpacity={0.6} width={126} />
      <Typography variant="h2">Collaborators</Typography>
      <Typography align="center" variant="body1">
        Invite teammates to collaborate and keep everything in one place.
      </Typography>
      <AlineButton color="primary" variant="contained" onClick={onAddCollaborator}>
        {addButtonLabel}
      </AlineButton>
    </div>
  );
}

function CollaboratorsList({
  permissions,
  templateTab,
  workflowId,
}: {
  permissions: ReturnType<typeof useGetWorkflowPermissions>['permissions'];
  templateTab: TemplateCollaboratorsTab | undefined;
  workflowId: UUID;
}) {
  const { classes } = useStyles();
  const sorted = sortCollaborators(permissions);

  return (
    <div className={classes.cardList}>
      {sorted.map(collaborator => (
        <WorkflowCollaboratorCard
          key={collaborator.id}
          collaborator={collaborator}
          templateTab={templateTab}
          variant={WorkflowCollaboratorCardVariant.Outline}
          workflowId={workflowId}
        />
      ))}
    </div>
  );
}

function AddCollaboratorContent({
  addButtonLabel,
  onAdded,
  permissions,
  timeliness,
  workflowId,
  workflowVariant,
}: {
  addButtonLabel: string;
  onAdded: () => void;
  permissions: ReturnType<typeof useGetWorkflowPermissions>['permissions'];
  timeliness: Apollo.WorkflowPermissionTimeliness;
  workflowId: UUID;
  workflowVariant: Apollo.WorkflowVariant;
}) {
  const { classes } = useStyles();
  const [message, setMessage] = useState('');
  const { org, orgMembers } = useGetOrgMembers({
    variables: { filter: Apollo.OrgMemberFilter.Active },
  });
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const { addWorkflowPermissions, loading: submitting } = useAddWorkflowPermissions();
  const [updateWorkflowPermission, { loading: updatingPermission }] =
    Apollo.useUpdateWorkflowPermissionMutation();

  const handleSubmit = useCallback(async () => {
    if (!accounts.length) return;

    const { accountsToCreate, permissionIdsToActivate } = getWorkflowPermissionAddPlan({
      accounts,
      permissions,
      timeliness,
      workflowVariant,
    });
    const props: Apollo.NewWorkflowPermissionProps[] = accountsToCreate.map(
      (account): Apollo.NewWorkflowPermissionProps => ({
        email: account.__typename === 'NewUser' ? account.email : null,
        orgId: account.__typename === 'Org' ? account.id : null,
        timeliness,
        userId: account.__typename === 'User' ? account.id : null,
      })
    );

    await Promise.all([
      props.length
        ? addWorkflowPermissions(workflowId, {
            variables: {
              input: {
                message: message || undefined,
                props,
                workflowId,
              },
            },
          })
        : Promise.resolve(),
      ...permissionIdsToActivate.map(workflowPermissionId =>
        updateWorkflowPermission({
          variables: {
            input: {
              message: message || undefined,
              props: { timeliness: Apollo.WorkflowPermissionTimeliness.Active },
              workflowPermissionId,
            },
          },
        })
      ),
    ]);
    if (!props.length && !permissionIdsToActivate.length) return;

    setAccounts([]);
    setMessage('');
    onAdded();
  }, [
    accounts,
    addWorkflowPermissions,
    message,
    onAdded,
    permissions,
    timeliness,
    updateWorkflowPermission,
    workflowVariant,
    workflowId,
  ]);

  return (
    <div className={classes.addForm}>
      <AccountsAutocomplete
        accounts={org ? [org, ...orgMembers] : orgMembers}
        getIsOptionDisabled={account => {
          return getIsAccountDisabledForWorkflowPermissionAdd({ account, permissions, timeliness });
        }}
        value={accounts}
        onChange={(_, value) => setAccounts(value)}
      />
      <AlineTextarea
        maxRows={10}
        minRows={5}
        placeholder="Add a message for new collaborators..."
        value={message}
        onChange={e => setMessage(e.target.value)}
      />
      <div className={classes.submitRow}>
        <AlineButton fullWidth color="primary" variant="outlined" onClick={onAdded}>
          Cancel
        </AlineButton>
        <AlineButton
          fullWidth
          color="primary"
          disabled={!accounts.length}
          loading={submitting || updatingPermission}
          variant="contained"
          onClick={handleSubmit}
        >
          {addButtonLabel}
        </AlineButton>
      </div>
    </div>
  );
}
